import React, { useState, useEffect } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import PrivateLayout from './PrivateLayout';
import { Header, Sidebar, Spinner } from '../components/common';
import { MatchAlert } from '../components/matches';
import EventJoinGoalsPrompt from '../components/events/EventJoinGoalsPrompt';
import { useAuth } from '../contexts/AuthContext';
import useIsDevice from '../hooks/useIsDevice';
import { routes } from '../lib/routes';

// Storage key for just-completed flag
const STORAGE_KEY_JUST_COMPLETED = 'onboarding_just_completed';

interface MainLayoutProps {
  children?: React.ReactNode;
}

const MainLayout = ({ children }: MainLayoutProps) => {
  const { isMobile, isTablet } = useIsDevice();

  const { user, isLoading } = useAuth();

  const [menuOpen, setMenuOpen] = useState(false);

  // BUG-010 FIX: Clear completion flag after dashboard loads
  // This allows the user to stay on dashboard even if onboarding_status is stale
  useEffect(() => {
    if (!user) return;

    // Check for user-scoped or legacy completion flag
    const scopedKey = `${STORAGE_KEY_JUST_COMPLETED}_${user.id}`;
    const hasCompletedFlag = sessionStorage.getItem(scopedKey) || sessionStorage.getItem(STORAGE_KEY_JUST_COMPLETED);

    if (hasCompletedFlag) {
      // Clear both flags after a short delay to allow dashboard to fully render
      // The delay prevents race conditions with navigation
      const timer = setTimeout(() => {
        sessionStorage.removeItem(scopedKey);
        sessionStorage.removeItem(STORAGE_KEY_JUST_COMPLETED);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [user]);

  // BUG-003 FIX: Show spinner instead of null while loading
  // This prevents the blank white screen flash
  if (isLoading) {
    return (
      <div className='min-h-screen flex items-center justify-center bg-white'>
        <Spinner size='lg' color='primary' />
      </div>
    );
  }

  // Unauthenticated users on a protected route: stash intended path,
  // redirect to login so it can resume post-auth.
  if (!user) {
    const intended = window.location.pathname + window.location.search;
    if (intended.startsWith('/') && !intended.startsWith('//') && intended !== routes.login) {
      sessionStorage.setItem('postLoginRedirect', intended);
    }
    return <Navigate to={routes.login} replace />;
  }

  // BUG-010 FIX: Don't redirect if just completed onboarding (flag present)
  // The user object might be stale, so check the flag first
  const scopedKey = `${STORAGE_KEY_JUST_COMPLETED}_${user.id}`;
  const hasJustCompleted = sessionStorage.getItem(scopedKey) || sessionStorage.getItem(STORAGE_KEY_JUST_COMPLETED);

  if (user.onboarding_status !== 'completed' && !hasJustCompleted) {
    return <Navigate to={routes.onboardingChat} replace />;
  }

  // UX-002 FIX: Don't render MainLayout for /onboarding/* routes
  // This prevents sidebar from flashing during onboarding completion flow
  const currentPath = window.location.pathname;
  if (currentPath.startsWith('/onboarding')) {
    return null; // OnboardingLayout will handle these routes
  }

  return (
    <PrivateLayout>
      <div className='min-h-screen bg-white'>
        <Sidebar menuOpen={menuOpen} setMenuOpen={setMenuOpen} />
        <div
          className={`relative h-full pt-[5rem] ml-auto ${isMobile || isTablet ? 'w-full' : 'w-[calc(100%-18rem)]'}`}
        >
          <Header
            className={`${isMobile || isTablet ? 'w-full' : 'w-[calc(100%-18rem)]'}`}
            menuOpen={menuOpen}
            setMenuOpen={setMenuOpen}
          />
          <div className='h-[calc(100dvh-5rem)] pb-4 overflow-y-auto'>
            <MatchAlert />
            <EventJoinGoalsPrompt />
            {children || <Outlet />}
          </div>
        </div>
      </div>
      {(isMobile || isTablet) && menuOpen && (
        <div
          className='fixed left-0 top-0 w-full h-full bg-black opacity-50 z-99'
          onClick={() => setMenuOpen(false)}
        ></div>
      )}
    </PrivateLayout>
  );
};

export default MainLayout;
